import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClientBanner, useVisitContext } from '../api/queries.ts';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import { VisitForm } from '../components/VisitForm.tsx';

/** Today, as a calendar day in the viewer's own zone — never via toISOString,
 *  which would roll over at 8pm in New York. */
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Record a visit: the page, its header and what follows a save. The form is components/VisitForm.tsx, lifted out
 * unchanged so Triage draws the same one (P-31, A-#286).
 */
export function RecordVisit() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  // The date is held here and handed to the form, so this check and the form read the same lookup (one request).
  const [visitDate, setVisitDate] = useState(todayLocal());
  const banner = useClientBanner(id);
  const where = useVisitContext(id, visitDate);

  // A form for a client that could not be loaded is not a form. The header
  // used to say "Loading…" for ever.
  const sessionError = [banner.error, where.error].find(
    (e) => e !== null && isSessionFailure(e, { forbiddenIsSession: false }),
  );
  const pageError = sessionError ?? (banner.error && !banner.data ? banner.error : null);
  if (pageError) {
    return (
      <div className="mx-auto min-h-dvh max-w-3xl border-x border-line bg-panel">
        <LoadFailure
          what="This client"
          error={pageError}
          onRetry={() => void banner.refetch()}
          retrying={banner.isFetching}
          forbiddenIsSession={false}
          badLink={{ says: 'This is not a valid client link.', back: { to: '/clients', label: 'Back to the client list' } }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto min-h-dvh max-w-3xl border-x border-line bg-panel">
      <header className="sticky top-0 z-10 bg-brand px-4 py-3 text-on-brand">
        <p className="text-xs font-bold tracking-[0.14em] uppercase">
          Record a visit
        </p>
        <h1 className="mt-0.5 text-xl leading-tight font-bold">
          {banner.data?.display_name ?? 'Loading…'}
        </h1>
      </header>

      <VisitForm
        clientId={id} date={visitDate} onDate={setVisitDate}
        // `replace`, so the submitted form is not left in the history. Pushing
        // meant back from the client's record returned to a form that had
        // already been saved — and re-submitting it would record the visit
        // twice. The state is what the record says happened when it arrives.
        onRecorded={() => navigate(`/clients/${id}`, {
          replace: true,
          state: { recorded: 'Visit recorded.' },
        })}
        onCancel={() => navigate(`/clients/${id}`)}
      />
    </div>
  );
}
